import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { FroalaTextEditor } from 'components/UIElements'

const froalaAnchorProps = {
  hasBlockStyles: true,
  spellCheck: true,
  moreText: { includeAll: true },
  moreParagraph: { includeAll: true },
}

/**
 * Left/right anchor editors flanking the scale (SelectView) — matches participant layout.
 */
const HorizontalSelectOneEditorRow = ({ item, itemId, updateItem, readOnly, children }) => {
  const left = item.attributes?.left_anchor ?? ''
  const right = item.attributes?.right_anchor ?? ''
  const anchorStateRef = useRef({ left, right })
  const scaleScrollRef = useRef(null)

  useEffect(() => {
    anchorStateRef.current = { left, right }
  }, [left, right])

  useEffect(() => {
    const el = scaleScrollRef.current
    if (!el) return

    const onWheel = e => {
      // If the browser is already doing horizontal scrolling, don't fight it.
      if (e.deltaX) return

      // Convert vertical wheel deltas into horizontal scroll when hovering the scale.
      // This is especially helpful on Windows when nested flex + overflow makes wheel->x mapping flaky.
      if (!e.deltaY) return

      const { scrollLeft, scrollWidth, clientWidth } = el
      const maxScrollLeft = scrollWidth - clientWidth
      if (maxScrollLeft <= 0) return

      const next = scrollLeft + e.deltaY
      const clamped = Math.max(0, Math.min(maxScrollLeft, next))
      if (clamped === scrollLeft) return

      e.preventDefault()
      el.scrollLeft = clamped
    }

    // Use a non-passive wheel listener so we can preventDefault when we take over scrolling.
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  const setAnchor = (key, html) => {
    const nextLeft = key === 'left_anchor' ? html : anchorStateRef.current.left
    const nextRight = key === 'right_anchor' ? html : anchorStateRef.current.right
    anchorStateRef.current = { left: nextLeft, right: nextRight }

    // Only update attributes so we never overwrite the question-level label
    // with a potentially stale `item` prop value during focus/blur changes.
    updateItem(itemId, {
      attributes: {
        ...item.attributes,
        left_anchor: nextLeft,
        right_anchor: nextRight,
      },
    })
  }

  return (
    <div className='horizontal-select-one-editor-row'>
      <div className='horizontal-select-one-editor-row__anchor horizontal-select-one-editor-row__anchor--left'>
        <FroalaTextEditor
          value={left}
          onChange={html => setAnchor('left_anchor', html)}
          readOnly={readOnly}
          showToolbar={!readOnly}
          placeholder='Low end (left of scale)'
          {...froalaAnchorProps}
        />
      </div>
      <div className='horizontal-select-one-editor-row__scale'>
        <div ref={scaleScrollRef} className='horizontal-select-one-editor-row__scale-scroll' tabIndex={0}>
          {children}
        </div>
      </div>
      <div className='horizontal-select-one-editor-row__anchor horizontal-select-one-editor-row__anchor--right'>
        <FroalaTextEditor
          value={right}
          onChange={html => setAnchor('right_anchor', html)}
          readOnly={readOnly}
          showToolbar={!readOnly}
          placeholder='High end (right of scale)'
          {...froalaAnchorProps}
        />
      </div>
    </div>
  )
}

HorizontalSelectOneEditorRow.propTypes = {
  children: PropTypes.node.isRequired,
  item: PropTypes.object.isRequired,
  itemId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  updateItem: PropTypes.func.isRequired,
}

export default HorizontalSelectOneEditorRow
